import { Copy, MessageCircle, ScanLine, Landmark, Send } from "lucide-react";
import { toast } from "sonner";
import { WHATSAPP_NUMBER, QRIS_URL } from "@/lib/site";
import { Reveal, Tilt } from "./motion-prims";

const STEPS = [
  { n: "01", title: "Pilih program", desc: "Telusuri program kerja, lihat kebutuhan dan targetnya." },
  { n: "02", title: "Transfer atau scan QRIS", desc: "Salurkan lewat rekening bank atau aplikasi pembayaran apa pun." },
  { n: "03", title: "Konfirmasi via WhatsApp", desc: "Kirim bukti transfer, donasi tercatat di dinding donatur." },
];

const BANKS = [
  { bank: "Bank BCA", number: "2480 6633 41", holder: "a.n. ALIKA SALSABILA SOPYAN", testid: "copy-bca-button" },
];

export default function HowToDonate() {
  const copy = async (number, testid) => {
    try {
      await navigator.clipboard.writeText(number.replace(/\s/g, ""));
      toast.success(`Nomor ${number} berhasil dikopi`);
    } catch {
      toast.error("Gagal mengopi nomor rekening");
    }
  };

  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Halo BANTU DONASI BERSAMA PR! Saya ingin konfirmasi donasi saya."
  )}`;

  return (
    <section id="cara-donasi" className="scroll-mt-20 relative bg-olive text-cream py-20 sm:py-28 overflow-hidden">
      <div className="sunburst-dark absolute -top-40 -right-40 w-[560px] aspect-square rounded-full pointer-events-none" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-gold-soft">Cara Donasi</p>
          <h2 className="mt-4 font-display font-black tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
            Tiga langkah, lima menit, <span className="italic text-gold-soft">satu dampak nyata.</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="h-full border-2 border-cream/25 rounded-2xl p-6 bg-olive-light/60">
                <p className="font-display font-black text-3xl text-gold-soft">{s.n}</p>
                <h3 className="mt-3 font-bold text-lg">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-cream/75">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-6 items-stretch">
          <Reveal>
            <Tilt max={6} className="h-full">
              <div className="h-full bg-cream text-ink border-2 border-ink rounded-2xl shadow-retro-gold p-6 sm:p-8 flex flex-col items-center justify-center text-center gap-5">
                <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">
                  <ScanLine className="w-4 h-4" /> QRIS — Semua Aplikasi Pembayaran
                </span>
                <img
                  src={QRIS_URL}
                  alt="QRIS P'R 25 — scan untuk donasi"
                  data-testid="qris-card"
                  className="w-56 sm:w-64 rounded-lg border-2 border-ink shadow-retro-sm"
                />
                <p className="text-sm text-pine leading-relaxed">
                  Scan dengan GoPay, OVO, DANA, LinkAja, atau mobile banking apa pun.
                  <span className="block mt-1 font-bold text-ink">Nominal bebas, mulai Rp 10.000.</span>
                </p>
              </div>
            </Tilt>
          </Reveal>

          <div className="flex flex-col gap-4 justify-center">
            {BANKS.map((b, i) => (
              <Reveal key={b.bank} delay={i * 0.06}>
                <div data-testid={`bank-card-${b.testid.split("-")[1]}`} className="flex items-center justify-between gap-4 border-2 border-cream/25 rounded-2xl px-5 sm:px-6 py-4 bg-olive-light/60">
                  <div className="flex items-center gap-4">
                    <span className="grid place-items-center w-11 h-11 rounded-full border-2 border-gold-soft text-gold-soft">
                      <Landmark className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="font-bold">{b.bank}</p>
                      <p className="font-display font-black text-lg sm:text-xl tracking-wide" data-testid={`bank-number-${b.testid.split("-")[1]}`}>
                        {b.number}
                      </p>
                      <p className="text-xs text-cream/60 mt-0.5">{b.holder}</p>
                    </div>
                  </div>
                  <button
                    data-testid={b.testid}
                    onClick={() => copy(b.number)}
                    className="shrink-0 grid place-items-center w-11 h-11 rounded-full border-2 border-cream/40 hover:border-gold-soft hover:text-gold-soft transition-colors"
                    aria-label={`Kopi nomor ${b.bank}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.2}>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wa-confirm-button"
                className="btn-retro bg-gold text-ink rounded-full px-7 py-4 text-sm sm:text-base w-full !shadow-[4px_4px_0_0_#F6F3EC]"
              >
                <MessageCircle className="w-5 h-5" /> Konfirmasi Donasi via WhatsApp
              </a>
              <p className="mt-3 text-xs text-cream/60 flex items-center gap-1.5">
                <Send className="w-3.5 h-3.5" /> Kirim bukti transfer — tim kami balas dan catat donasi Anda.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
